import { TranslateService } from "@ngx-translate/core";

import { PacketType } from "../../../apis/stats/rest/models/packet-type";
import { ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { CustomDropdownOption } from "../../common/custom-dropdown/custom-dropdown";
import { COLOR_BLUE, COLOR_LIGHT_BLUE, COLOR_LIGHT_RED, COLOR_RED } from "../../common/line-chart/colors";
import { LineChartLines } from "../../common/line-chart/line-chart.component";

export interface TrafficSubject {
  subscriptionId: string;
  // `subscriptionInstanceIds` can be left undefined for when we don't want to filter on them.
  // Using an empty array instead leads to sending a query parameter without a value, which is
  // not what we want.
  subscriptionInstanceIds?: string[];
  name: string;
}

export type TrafficDropDownOption = CustomDropdownOption & { subject?: TrafficSubject };

export const LINE_METADATA: Record<string, { color: string; label: string }> = {
  peakIn: { color: COLOR_LIGHT_BLUE, label: "Chart.Label.PeakIn" },
  peakOut: { color: COLOR_LIGHT_RED, label: "Chart.Label.PeakOut" },
  in: { color: COLOR_BLUE, label: "Chart.Label.In" },
  out: { color: COLOR_RED, label: "Chart.Label.Out" },
};

/**
 * Phrases an option for the traffic chart's select box to request all of one service's traffic.
 *
 * A service option is characterized by not specifying the subscription instance ids,
 * in effect requesting traffic of all ports underlying the service.
 */
export function makeNamedOption(s: {
  subscriptionId: string;
  description: string;
  subscriptionInstanceId?: string;
}): TrafficSubject {
  return { subscriptionId: s.subscriptionId, name: s.description };
}

export function makeAllTrafficOption(subscriptionId: string, translate: TranslateService): TrafficSubject {
  return {
    subscriptionId,
    name: translate.instant("Chart.AllTraffic") as unknown as string,
  };
}

/**
 * Phrases an option for the traffic chart's select box to request all services' traffic on one port.
 *
 * A ports' all traffic option in effect just requests all traffic of a port, but this specific
 * variant is characterized by a specific option name / label 'All services' instead of the port
 * description.
 */
export function makeAllServicesOption(subscriptionId: string, translate: TranslateService): TrafficSubject {
  return {
    subscriptionId,
    name: translate.instant("Chart.AllServices") as unknown as string,
  };
}

export const EMPTY_TRAFFIC: LineChartLines = { peakIn: [], peakOut: [], in: [], out: [] };

export const PACKET_TYPE_OPTIONS = [
  { id: 0, name: "Unicast", type: PacketType.Unicast },
  { id: 1, name: "Multicast", type: PacketType.Multicast },
  { id: 2, name: "Broadcast", type: PacketType.Broadcast },
];

export function makeBpsCustomization(translate: TranslateService) {
  return {
    formatPoint(series: string, y: number) {
      const v = new ReadableSizePipe().transform(y);
      const label = translate.instant(LINE_METADATA[series].label) as string;
      return `<span style="color: ${LINE_METADATA[series].color}">●</span> ${label}: <b>${v}</b><br/>`;
    },
  };
}

export function makePpsCustomization(translate: TranslateService) {
  return {
    formatPoint(series: string, y: number) {
      const v = `${Math.abs(Math.round(y)).toLocaleString()} pps`;
      const label = translate.instant(LINE_METADATA[series].label) as string;
      return `<span style="color: ${LINE_METADATA[series].color}">●</span> ${label}: <b>${v}</b><br/>`;
    },
  };
}
